import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ComplianceTimers } from '../components/projects/ComplianceTimers';
import { MilestoneTracker } from '../components/projects/MilestoneTracker';
import { formatCurrency } from '../utils/helpers';

export const ProjectDetail = () => {
  const { id } = useParams();
  const { data } = useApp();

  const project = data.projects.find(p => p.id === id) || data.projects[0];

  const milestones = [
    {
      name: 'Site Mobilization',
      dueDate: '2024-07-01',
      status: 'Completed',
      evidence: [
        {
          name: 'MOB_CERT.pdf',
          hash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
          type: 'application/pdf',
          uploadedAt: '2024-07-02T10:30:00Z',
          metadata: {
            description: 'Official mobilization certificate signed by site engineer',
            uploadedBy: 'John Kamati',
            witnessName: 'Maria Shikongo'
          }
        },
        {
          name: 'site_photo_1.jpg',
          hash: '8f434346648f6b96df89dda901c5176b10a6d83961dd3c1ac88b59b2ef41a729',
          type: 'image/jpeg',
          uploadedAt: '2024-07-02T14:15:00Z',
          metadata: {
            description: 'Site mobilization - equipment and materials on site',
            uploadedBy: 'John Kamati',
            gpsLocation: '-22.5609, 17.0658'
          }
        }
      ]
    },
    {
      name: 'Earthworks & Foundation',
      dueDate: '2024-10-15',
      status: 'Completed',
      evidence: [
        {
          name: 'FOUNDATION_INSP.jpg',
          hash: '8f434346648f6b96df89dda901c5176b10a6d83961dd3c1ac88b59b2ef41a729',
          type: 'image/jpeg',
          uploadedAt: '2024-10-16T09:20:00Z',
          metadata: {
            description: 'Foundation inspection completed, all structural tests passed',
            uploadedBy: 'Sarah Nambahu'
          }
        }
      ]
    },
    {
      name: 'Structural Framing',
      dueDate: '2025-02-01',
      status: 'On Track',
      evidence: []
    },
    {
      name: 'Roofing & Utilities',
      dueDate: '2025-06-15',
      status: 'Pending',
      evidence: []
    }
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <nav className="flex items-center gap-2 text-xs text-slate-500 mb-4">
        <Link to="/projects" className="hover:text-grn-navy">Projects</Link>
        <SafeIcon icon={FiIcons.FiChevronRight} />
        <span className="font-bold text-slate-800">{project.id}</span>
      </nav>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-slate-900">{project.name}</h1>
            <Badge status={project.status} />
          </div>
          <p className="text-sm text-slate-500 font-medium">{project.ministry}</p>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiDownload} />
            Audit Pack
          </button>
          <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900">
            Update Progress
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Statutory Compliance Countdowns">
            <ComplianceTimers project={project} />
          </Card>

          <Card 
            title="Project Milestones & Evidence" 
            action={
              <button className="text-xs font-bold text-grn-navy hover:underline">
                Add Milestone
              </button>
            }
          >
            <MilestoneTracker milestones={milestones} />
          </Card>

          <Card title="Field Evidence (Recent Photos)">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div 
                  key={i} 
                  className="aspect-square bg-slate-100 rounded-lg border border-slate-200 flex flex-col items-center justify-center text-slate-400 overflow-hidden relative group"
                >
                  <img 
                    src={`https://images.unsplash.com/photo-1503387762-592dea58ef23?auto=format&fit=crop&w=300&q=80`}
                    alt="Site"
                    className="object-cover w-full h-full opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/50 text-[10px] text-white">
                    Logged: 24/02/2026
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Investment Summary">
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Contract Value</p>
                <p className="text-xl font-black text-slate-900">{formatCurrency(project.value)}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Disbursed to Date</p>
                <p className="text-xl font-black text-grn-green">
                  {formatCurrency(project.value * (project.progress / 100))}
                </p>
                <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2">
                  <div 
                    className="bg-grn-green h-1.5 rounded-full" 
                    style={{ width: `${project.progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Key Entities">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 text-blue-600 rounded">
                  <SafeIcon icon={FiIcons.FiUser} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Contractor</p>
                  <p className="text-sm font-bold text-slate-800">{project.contractor}</p>
                  <p className="text-xs text-slate-500">Score: 88/100</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-50 text-amber-600 rounded">
                  <SafeIcon icon={FiIcons.FiShield} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">PPU Auditor</p>
                  <p className="text-sm font-bold text-slate-800">Mr. T. Shivute</p>
                  <p className="text-xs text-slate-500">Assigned: Nov 2024</p>
                </div>
              </div>
            </div>
          </Card>

          <Card title="NDP6 Alignment">
            <div className="p-3 bg-grn-navy/5 rounded-lg border border-grn-navy/10">
              <p className="text-[10px] font-bold text-grn-navy uppercase opacity-60">Pillar</p>
              <p className="text-sm font-bold text-grn-navy">{project.pillar}</p>
              <div className="mt-2 text-xs text-slate-600 italic">
                Focus Area: Infrastructure Modernization
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};